// The canonical scan-path resolvers (slice 04-B-3).
//
//   /q/wc/{uuid} — module 02's station QR (spec 04 §3.3 step 2): pin the
//     station and land on S9. The pin happens in the ROUTE (beforeLoad,
//     router.tsx); this file only holds the op resolver.
//   /q/op/{uuid} — §5 e29's traveler per-op QR: "scanning it in the PWA opens
//     that op's run screen when the op belongs to the device's pinned
//     station, else shows a hint — the station QR remains the auth/context
//     anchor, the op QR is a shortcut only." The snapshot of the PINNED
//     station is the only surface a floor session may read, so when the op is
//     not in it the honest answer is "scan your station QR first" — the
//     client cannot name the op's own station without a read fence it does
//     not hold, and guessing one would be worse.
//
// SEAM: scan.ts's wc/op recognisers (the settle-fix sub-agent's scope) feed
// these SAME routes from keyboard-wedge input; the routes are the shared
// contract between the two halves — `/q/wc/$workCenterId`, `/q/op/$opId`.

import { useEffect } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { FloorEmpty, FloorScreen, FloorSkeleton, SnapshotNote } from "../components/floor-chrome";
import { useStationSnapshot } from "../hooks/use-station";
import { pinnedStation } from "../lib/production-floor";
// This route renders OUTSIDE the /floor layout (the path is the printed
// /q/op/… URL), so the floor styles are pulled in here.
import "./floor-inventory.css";
import "./floor-production.css";

export function QrOpResolverScreen() {
  const { t } = useTranslation();
  const { opId } = useParams({ from: "/q/op/$opId" });
  const navigate = useNavigate();
  const pinned = pinnedStation();
  const read = useStationSnapshot(pinned?.workCenterId ?? "");
  const op = read.data?.queue.operations.find((candidate) => candidate.id === opId);

  useEffect(() => {
    if (pinned !== undefined && op !== undefined) {
      void navigate({
        to: "/floor/station/$workCenterId/op/$opId",
        params: { workCenterId: pinned.workCenterId, opId },
        replace: true,
      });
    }
  }, [pinned, op, opId, navigate]);

  return (
    <FloorScreen
      title={t("production.floor.qr.title")}
      back={
        <Link to="/floor/station" className="floor-back">
          {t("production.floor.station.changeStation")}
        </Link>
      }
    >
      {pinned === undefined ? (
        <FloorEmpty
          title={t("production.floor.qr.noStationTitle")}
          body={t("production.floor.qr.noStationBody")}
        />
      ) : (
        <>
          <SnapshotNote source={read.source} cachedAt={read.cachedAt} />
          {read.isLoading && read.data === undefined ? <FloorSkeleton rows={2} /> : null}
          {read.data !== undefined && op === undefined ? (
            <FloorEmpty
              title={t("production.floor.qr.elsewhereTitle")}
              body={t("production.floor.qr.elsewhereBody")}
            />
          ) : null}
          {read.data === undefined && !read.isLoading ? (
            <FloorEmpty
              title={t("production.floor.qr.noSnapshotTitle")}
              body={t("production.floor.qr.noSnapshotBody")}
            />
          ) : null}
        </>
      )}
    </FloorScreen>
  );
}
